/**
 * Page lifecycle helpers used to protect the session during a page refresh.
 *
 * When the user refreshes (or navigates away) while requests such as the
 * splash / home APIs are still in flight, browsers abort those requests.
 * Depending on the browser (Firefox / Safari in particular) the old page keeps
 * running JS for a short time and the aborted requests reject with a
 * "Network Error" / "Request aborted" — or the server returns a transient
 * failure for the duplicated requests. None of these mean the session is
 * invalid, so they must never trigger an error toast or a logout.
 */

let pageUnloading = false;

if (typeof window !== "undefined") {
  const markUnloading = () => {
    pageUnloading = true;
  };
  window.addEventListener("beforeunload", markUnloading);
  window.addEventListener("pagehide", markUnloading);
  // Page restored from the back/forward cache — it is alive again.
  window.addEventListener("pageshow", () => {
    pageUnloading = false;
  });
}

/** True once the current document has started unloading (refresh / navigation). */
export const isPageUnloading = () => pageUnloading;

/**
 * True when a request failed because it was cancelled/aborted or never got a
 * response (e.g. browser aborted it during refresh), not because the server
 * rejected it.
 */
export const isAbortedRequestError = (error) => {
  if (!error) return false;
  if (error.name === "AbortError" || error.name === "CanceledError") return true;
  if (error.code === "ERR_CANCELED" || error.code === "ECONNABORTED") return true;
  // Network-level failure with no HTTP response while the page is unloading.
  if (!error.response && pageUnloading) return true;
  return false;
};
